/**
 * API Client Service for Evaluation endpoints
 *
 * Handles HTTP requests for groundtruth evaluation features.
 */

import axios from 'axios';
import { EvaluationMetrics, GenerateEvaluatorRequest, GenerateEvaluatorResponse } from '../types/evaluation';

// Base URL from environment variable, default to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Handle API errors and transform them into user-friendly messages
 */
function handleApiError(error: unknown, entityType: string = 'resource'): Error {
  if (axios.isAxiosError(error)) {
    const axiosError = error;

    // Network or timeout error
    if (!axiosError.response) {
      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timed out. Please try again.');
      }
      return new Error('Unable to connect to the server. Please check your connection.');
    }

    // Server returned an error response
    const status = axiosError.response.status;
    const data = axiosError.response.data;

    // Extract error message from response
    const message = data?.detail || data?.message || axiosError.message;

    switch (status) {
      case 400:
        return new Error(`Validation error: ${message}`);
      case 404:
        return new Error(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} not found.`);
      case 409:
        return new Error(message);
      case 500:
        return new Error('An error occurred on the server. Please try again later.');
      default:
        return new Error(message || 'An unexpected error occurred.');
    }
  }

  // Non-axios error
  return error instanceof Error ? error : new Error('An unexpected error occurred.');
}

/**
 * Evaluation Service - API client methods for evaluation operations
 */
export const evaluationService = {
  /**
   * Generate evaluator function from natural language description
   */
  generateEvaluator: async (
    batchId: string,
    data: GenerateEvaluatorRequest
  ): Promise<GenerateEvaluatorResponse> => {
    try {
      const response = await apiClient.post<GenerateEvaluatorResponse>(
        `/api/batches/${batchId}/generate-evaluator`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'evaluator generation');
    }
  },

  /**
   * Get evaluation metrics for a batch
   */
  getEvaluationMetrics: async (batchId: string): Promise<EvaluationMetrics> => {
    try {
      const response = await apiClient.get<EvaluationMetrics>(
        `/api/batches/${batchId}/evaluation-metrics`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'evaluation metrics');
    }
  },
};
